/*
 * Copyright (C) 2024 S&P Global.
 * All Rights Reserved
 */
import { AppIdentifier } from "@finos/fdc3";
import { MessageHeaderService } from "./MessageHeaderService";
import { MessageService } from "./MessageService";
import { AsyncRequestResolver, RouteDefinition, RouteHandler, RoutePattern } from "../types/routing";
import { FusionInteropMessage, MessageType, SystemMessageExchange, SystemMessagePayload, TargetableWindow, VerifiablePayload } from "../types/message";
import { AppType } from "../types";

export class InteropService {
  public get isInitialized(): boolean {
    return this._isInitialized;
  }

  public get sourceAppType(): AppType {
    return this._messageService.sourceAppType;
  }

  public get sourceAppIdentifier(): AppIdentifier {
    if (!this._sourceAppIdentifier) {
      throw new Error("Attempted to access sourceAppIdentifier before it has been set (InteropService has not been properly initialized)");
    }
    return this._sourceAppIdentifier as AppIdentifier;
  }

  private _messageService: MessageService;
  private _messageHeaderService: MessageHeaderService;
  private _isInitialized = false;
  private _asyncRequestResolvers: AsyncRequestResolver[] = [];
  private _defaultRequestTimeoutMs = 5000; //TODO - Get value from a new property in config (when available) instead of hardcoding
  private _routeDefinitions: RouteDefinition[] = [];
  private _sourceAppIdentifier: AppIdentifier | undefined;

  // prettier-ignore
  constructor(messageService: MessageService,
              messageHeaderService: MessageHeaderService) {
    console.log("InteropService.constructor");
    this._messageService = messageService;
    this._messageHeaderService = messageHeaderService;
    // Create built-in route definition to handle the routing of system message responses
    this._routeDefinitions.push(this._getSystemMessageResponseRouteDefinition());
    // Add system message listener
    this._messageService.addMessageListener(this._messageListener.bind(this));
  }

  public initialize(sourceAppIdentifier: AppIdentifier): void {
    console.log("InteropService.initializeSourceAppIdentifier");
    if (this._isInitialized) {
      throw new Error("InteropService has already been initialized");
    }
    if (!sourceAppIdentifier) {
      throw new Error("sourceAppIdentifier not provided");
    }
    this._sourceAppIdentifier = sourceAppIdentifier;
    this._isInitialized = true;
  }

  public async request<TResponsePayload extends SystemMessagePayload>(payload: SystemMessagePayload, targetAppType: AppType, targetAppIdentifier?: AppIdentifier): Promise<VerifiablePayload<TResponsePayload>> {
    console.log("InteropService.request");
    if (!this._sourceAppIdentifier) {
      throw new Error("Cannot request until source appIdentifier has been initialized");
    }
    if (targetAppType === this._messageService.sourceAppType) {
      throw new Error("Cannot request from self");
    }
    const requestMessage: FusionInteropMessage<SystemMessagePayload> = {
      type: MessageType.spgiFusionSystem,
      header: this._messageHeaderService.createHeader(this._messageService.sourceAppType, targetAppType, this._sourceAppIdentifier, targetAppIdentifier),
      payload,
    };
    console.log("requestMessage");
    console.log(requestMessage);

    return new Promise<VerifiablePayload<TResponsePayload>>((resolve, reject) => {
      //TODO - Error handling e.g. catch error and reject the promise
      const timeoutId = this._messageService.browserGlobalScope.setTimeout(() => {
        // Stop tracking the async request resolver because we are rejecting it
        this._asyncRequestResolvers = this._asyncRequestResolvers.filter((arr) => arr.correlationId !== requestMessage.header.correlationId);
        // Reject the promise due to request timeout
        console.log("request timed out");
        console.log(requestMessage);
        reject(`InteropService async request timed out (correlationId: ${requestMessage.header.correlationId})`);
      }, this._defaultRequestTimeoutMs);

      const asyncRequestResolver: AsyncRequestResolver = {
        sourceAppType: this._messageService.sourceAppType,
        targetAppType,
        correlationId: requestMessage.header.correlationId,
        timeoutId,
        resolve,
        reject,
      };
      this._asyncRequestResolvers.push(asyncRequestResolver);

      // Publish request message to target appIdentifier or target appType
      this._messageService.publish(requestMessage, targetAppIdentifier ?? targetAppType);

      // Note: We do not resolve the promise here; instead resolution is handled in the systemMessageResponseRouteHandler which handles all system async response messages.
      // That listener will have access to the resolve function for this promise in this._asyncRequestResolvers.
    });
  }

  public respond(responsePayload: SystemMessagePayload, correspondingRequestMessage: FusionInteropMessage<SystemMessagePayload>, correspondingRequestTargetableWindow?: TargetableWindow): void {
    console.log("InteropService.respond");
    if (!this._sourceAppIdentifier) {
      throw new Error("Cannot request until source appIdentifier has been initialized");
    }
    if (!correspondingRequestMessage) {
      throw new Error("correspondingRequestMessage not provided");
    }
    if (correspondingRequestMessage.header.originatingAppType === this._messageService.sourceAppType) {
      throw new Error("Cannot respond to self");
    }
    const targetAppType = correspondingRequestMessage.header.originatingAppType;
    const targetAppIdentifier = correspondingRequestMessage.header.originatingAppIdentifier;
    if (!targetAppIdentifier || !targetAppIdentifier.appId || !targetAppIdentifier.instanceId) {
      throw new Error("Failed to publish response message due to incomplete originatingAppIdentifier on correspondingRequestMessage header");
    }
    const responseMessage: FusionInteropMessage<SystemMessagePayload> = {
      type: MessageType.spgiFusionSystem,
      header: this._messageHeaderService.createHeader(this._messageService.sourceAppType, targetAppType, this._sourceAppIdentifier, targetAppIdentifier, correspondingRequestMessage),
      payload: responsePayload,
    };
    // console.log("responseMessage");
    // console.log(responseMessage);
    // Publish response message to targetable window or target appIdentifier
    this._messageService.publish(responseMessage, correspondingRequestTargetableWindow ?? targetAppIdentifier);
  }

  public send(payload: SystemMessagePayload, targetAppType: AppType, targetAppIdentifier?: AppIdentifier): void {
    console.log("InteropService.send");
    if (!this._sourceAppIdentifier) {
      throw new Error("Cannot request until source appIdentifier has been initialized");
    }
    if (targetAppType === this._messageService.sourceAppType) {
      throw new Error("Cannot send to self");
    }
    const message: FusionInteropMessage<SystemMessagePayload> = {
      type: MessageType.spgiFusionSystem,
      header: this._messageHeaderService.createHeader(this._messageService.sourceAppType, targetAppType, this._sourceAppIdentifier, targetAppIdentifier),
      payload,
    };
    console.log("send message");
    console.log(message);
    // Publish request message to target appIdentifier or target appType
    this._messageService.publish(message, targetAppIdentifier ?? targetAppType);
  }

  public addRouteHandler(routePattern: RoutePattern, routeHandler: RouteHandler): void {
    //TODO - Consider validating whether the routePattern has already been used by a previous routeHandler (perhaps invoke console.warn?)
    //TODO - Consider disallowing systemMessageExchange for system messages in routePattern (only allow systemMessageType)
    console.log("InteropService.addRouteHandler");
    if (!routePattern || !routePattern.type) {
      throw new Error("routePattern not provided");
    }
    if (routePattern.type === MessageType.spgiFusionSystem && !routePattern.systemMessageType) {
      throw new Error("systemMessageType must be provided in routePattern when type is system message");
    }
    if (!routeHandler) {
      throw new Error("routeHandler not provided");
    }
    if (typeof routeHandler !== "function") {
      throw new Error("routeHandler is not a function");
    }
    const routeDefinition: RouteDefinition = {
      pattern: routePattern,
      handler: routeHandler,
    };
    this._routeDefinitions.push(routeDefinition);
  }

  private _getSystemMessageResponseRouteDefinition(): RouteDefinition {
    console.log("InteropService._getSystemMessageResponseRouteDefinition");
    const systemMessageResponseRouteHandler: RouteHandler = (responseMessage: FusionInteropMessage<SystemMessagePayload>,
                                                             responseOrigin: string,
                                                             responseSourceWindow: WindowProxy): void => {
      const asyncRequestResolver: AsyncRequestResolver | undefined = this._asyncRequestResolvers.find((arr) => arr.correlationId === responseMessage.header.correlationId);
      if (!asyncRequestResolver) {
        console.error(`${this._messageService.sourceAppType} InteropService unable to find async request resolver for correlationId '${responseMessage.header.correlationId}'`);
        console.log(this._messageService.browserGlobalScope.location?.href);
        console.log("asyncRequestResolvers correlationId values:");
        console.log(this._asyncRequestResolvers.map((arr) => arr.correlationId));
        // console.error(responseMessage);
        console.log(JSON.stringify(responseMessage, undefined, 2));
      } else {
        console.log("responseMessage (via systemMessageResponseRouteHandler)");
        console.log(responseMessage);
        // Stop tracking the async request resolver because we are resolving it
        this._asyncRequestResolvers = this._asyncRequestResolvers.filter((arr) => arr.correlationId !== responseMessage.header.correlationId);
        // Clear the request timeout handler
        if (asyncRequestResolver.timeoutId) {
          this._messageService.browserGlobalScope.clearTimeout(asyncRequestResolver.timeoutId);
        }
        // Resolve the promise
        if (typeof asyncRequestResolver.resolve === "function") {
          const verifiablePayload: VerifiablePayload<SystemMessagePayload> = {
            payload: responseMessage.payload,
            origin: responseOrigin,
            sourceWindow: responseSourceWindow,
          };
          asyncRequestResolver.resolve(verifiablePayload);
        }
      }
    };
    return {
      pattern: {
        type: MessageType.spgiFusionSystem,
        systemMessageExchange: SystemMessageExchange.response,
      },
      handler: systemMessageResponseRouteHandler,
    } as RouteDefinition;
  }

  private async _messageListener(event: MessageEvent<FusionInteropMessage<SystemMessagePayload>>): Promise<void> {
    // console.log("InteropService._messageListener");
    // console.log(event);
    if (!event) {
      throw new Error("Unexpected empty event in InteropService message listener");
    } else if (!event.data) {
      throw new Error("Unexpected empty event data in InteropService message listener");
    }

    const message = event.data;
    // console.log(message);
    if (message.type === MessageType.spgiFusionSystem) {
      // console.log(`messageType: ${message.type}`);
      if (!message.header) {
        throw new Error("Unexpected empty message header in InteropService message listener");
      }
      if (!message.payload) {
        throw new Error("Unexpected empty message payload in InteropService message listener");
      }
      message.header.receivedTimestamp = Date.now();
      for (const routeDefinition of this._routeDefinitions) {
        if (message.type === routeDefinition.pattern.type) {
          let messageMatchesPattern = false;
          if (message.type !== MessageType.spgiFusionSystem) {
            messageMatchesPattern = true;
          } else {
            // For system messages we also need to match on system message type
            const payload = message.payload as SystemMessagePayload;
            if (routeDefinition.pattern.systemMessageExchange && payload.type.toString().toLowerCase().endsWith(routeDefinition.pattern.systemMessageExchange)) {
              messageMatchesPattern = true;
            } else if (payload.type === routeDefinition.pattern.systemMessageType) {
              messageMatchesPattern = true;
            }
          }
          if (messageMatchesPattern) {
            try {
              await routeDefinition.handler(message, event.origin, event.source as WindowProxy);
            } catch (e) {
              console.error(e);
            }
          }
        }
      }
    }
  }
}
